/**
 * Preferences › Voice › Vocabulary › one word: its spelling (saved on blur
 * and Enter; a spelling another word has reverts) and how it was misheard.
 * The header keeps the name the word had when the page opened.
 */
import { useEffect, useRef, useState } from "react";
import {
  Button,
  ChipTextInput,
  PocketSection,
  RemovableChip,
} from "@/components/ui";
import type { ApiError } from "@/api/client";
import { VOCABULARY_TEXT_MAX } from "@/api/voice-vocabulary";
import { useT } from "@/i18n";
import { settingsVoiceVocabularyMessages } from "@/i18n/messages/settings-voice-vocabulary";
import { useDictationVocabularyStore } from "@/stores/dictation-vocabulary-store";
import { useSettingsNav } from "./settings-navigation";
import { SettingsFieldError, SettingsTextField } from "./settings-text-field";

const status = (error: unknown) => (error as Partial<ApiError>)?.status;

export function VoiceVocabularyWordPage({ wordId }: { wordId: string }) {
  const nav = useSettingsNav();
  const t = useT(settingsVoiceVocabularyMessages);
  const stored = useDictationVocabularyStore((state) =>
    state.words.find((entry) => entry.id === wordId),
  );
  const loaded = useDictationVocabularyStore(
    (state) => state.loadedAt !== null,
  );
  const renameWord = useDictationVocabularyStore((state) => state.renameWord);
  const removeWord = useDictationVocabularyStore((state) => state.removeWord);
  const addHeardAs = useDictationVocabularyStore((state) => state.addHeardAs);
  const removeHeardAs = useDictationVocabularyStore(
    (state) => state.removeHeardAs,
  );
  const [error, setError] = useState<string | null>(null);
  // While Remove word is in flight the word stays on screen, so a refusal
  // can say so here and the page stays open.
  const [removing, setRemoving] = useState(false);
  const lastSeen = useRef(stored);
  if (stored) lastSeen.current = stored;
  const word = stored ?? (removing ? lastSeen.current : undefined);
  const text = word?.text ?? "";
  const [draft, setDraft] = useState(text);
  const missing = word === undefined;
  const leaving = useRef(false);

  useEffect(() => setDraft(text), [text]);

  // Removed on another computer or in another window: nothing left to show.
  useEffect(() => {
    if (missing && loaded && !leaving.current) {
      leaving.current = true;
      nav.back();
    }
  }, [missing, loaded, nav]);

  if (!word) return null;

  function rename() {
    const value = draft.trim();
    if (!word || !value || value === word.text) {
      setDraft(text);
      return;
    }
    setError(null);
    renameWord(word.id, value).catch((reason: unknown) => {
      setDraft(text);
      setError(
        status(reason) === 409 ? t("renameConflict") : t("renameFailed"),
      );
    });
  }

  function addHeard(heard: string) {
    if (!word) return;
    setError(null);
    addHeardAs(word.id, heard).catch((reason: unknown) =>
      setError(status(reason) === 400 ? t("heardAsInvalid") : t("addFailed")),
    );
  }

  function removeHeard(heardId: string) {
    if (!word) return;
    setError(null);
    removeHeardAs(word.id, heardId).catch(() => setError(t("removeFailed")));
  }

  return (
    <div data-testid="settings-page-preferences-voice-vocabulary-word">
      <PocketSection>
        <SettingsTextField
          label={t("word")}
          value={draft}
          maxLength={VOCABULARY_TEXT_MAX}
          onChange={(event) => setDraft(event.target.value)}
          onBlur={rename}
          onKeyDown={(event) => {
            if (event.key !== "Enter") return;
            event.preventDefault();
            rename();
          }}
          testId="voice-vocabulary-word-text"
        />
      </PocketSection>
      <PocketSection title={t("heardAs")} testId="voice-vocabulary-heard-as">
        <div className="flex flex-wrap gap-1 px-2 pb-1.5 pt-0.5">
          {word.heard_as.map((heard) => (
            <RemovableChip
              key={heard.id}
              testId={`voice-vocabulary-heard-${heard.id}`}
              label={heard.text}
              removeLabel={t("removeHeard", { text: heard.text })}
              onRemove={() => removeHeard(heard.id)}
            />
          ))}
          <ChipTextInput
            testId="voice-vocabulary-heard-add"
            placeholder={t("add")}
            onSubmit={addHeard}
          />
        </div>
        <p className="p-2 text-appkit-small text-muted">
          {t("heardAsFootnote")}
        </p>
        <SettingsFieldError
          error={error}
          testId="voice-vocabulary-word-error"
        />
      </PocketSection>
      <div className="mt-3 flex items-center px-2">
        <Button
          variant="ghost"
          size="sm"
          className="text-danger hover:text-danger"
          disabled={removing}
          onClick={() => {
            setError(null);
            setRemoving(true);
            removeWord(word.id).then(
              () => {
                leaving.current = true;
                nav.back();
              },
              () => {
                setRemoving(false);
                setError(t("removeWordFailed"));
              },
            );
          }}
          data-testid="voice-vocabulary-word-remove"
        >
          {t("removeWord")}
        </Button>
      </div>
    </div>
  );
}
